import React, { useRef } from 'react';
import FocusTrap from 'focus-trap-react';
import { useModal } from '@repo/ui/src/modules/UI';
import { classNames } from '@repo/ui/src/utils/classNames';
import { DrawerContext } from '@repo/ui/src/modules/UI/context/DrawerContext';
import { ModalID } from '../../';

export interface DrawerProps {
  children: React.ReactNode;
  id: ModalID;
  disableOutsideClose?: boolean;
  className?: string;
}

export const Drawer = ({
  id,
  children,
  disableOutsideClose = false,
  className = '',
}: DrawerProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [active, setActive] = React.useState(false);

  const { zIndex, active: isVisible, close, closeAll, isTopModal, index } = useModal(id);

  React.useEffect(() => {
    const handler = (e: Event) => {
      if (!ref.current) {
        return;
      }
      if (document.contains(e.target as Node) && !ref.current.contains(e.target as Node)) {
        close();
      }
    };
    if (!disableOutsideClose && isVisible && isTopModal) {
      window.setTimeout(() => window.addEventListener('click', handler));
    }
    return () => window.removeEventListener('click', handler);
  }, [disableOutsideClose, isVisible, close, id, isTopModal]);

  React.useEffect(() => {
    const el = ref.current;
    const runHandler = (e: TransitionEvent) => {
      const target = e.currentTarget as HTMLElement;
      if (e.propertyName === 'transform' && target.dataset.visible === 'true') {
        setActive(true);
        // modals must receive focus when they open
        target.focus();
      }
    };
    const endHandler = (e: TransitionEvent) => {
      const target = e.currentTarget as HTMLElement;
      if (e.propertyName === 'transform' && target.dataset.visible === 'false') {
        setActive(false);
      }
    };
    if (el) {
      el.addEventListener('transitionrun', runHandler);
      el.addEventListener('transitionend', endHandler);
      return () => {
        el.removeEventListener('transitionrun', runHandler);
        el.removeEventListener('transitionend', endHandler);
      };
    }
  }, [ref]);

  return (
    <FocusTrap active={active} focusTrapOptions={{ allowOutsideClick: !disableOutsideClose }}>
      <div
        role="dialog"
        ref={ref}
        className={classNames(
          'tw-fixed tw-inset-0 tw-bg-white tw-h-screen tw-shadow-drawer tw-overflow-y-auto tw-transition tw-duration-500 sm:tw-left-auto',
          !isVisible && 'tw-translate-y-full sm:tw-translate-y-0 sm:tw-translate-x-full',
          !active && 'tw-invisible',
          zIndex,
          className,
        )}
        data-visible={isVisible}
        data-testid="drawer-container"
        aria-modal="true"
        aria-hidden={!isVisible}
        aria-labelledby={`${id}-drawer-title`}
      >
        <DrawerContext.Provider value={{ inDrawer: true, close, closeAll, id, index }}>
          {children}
        </DrawerContext.Provider>
      </div>
    </FocusTrap>
  );
};
